import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";

import {
  useCreateInspectionMutation,
  useGetInspectionByRegistrationId,
  useUpdateInspectionMutation,
} from "../../../../../../api/jeep-rally/inspections/inspections.hooks";
import {
  buildInspectionPayload,
  createEmptyInspectionForm,
  getDriverName,
  getEntityId,
  getRegistrationApiErrorMessage,
  getTeamName,
  INSPECTION_FIELD_DEFS,
  normalizeInspection,
} from "../utils/competitors.utils";

const InspectionModal = ({
  show,
  registration,
  readOnly = false,
  onClose,
  onSaved,
}) => {
  const registrationId = getEntityId(registration);
  const [form, setForm] = useState(createEmptyInspectionForm());
  const [formError, setFormError] = useState("");

  const {
    data: inspectionData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetInspectionByRegistrationId(registrationId, {
    enabled: show && Boolean(registrationId),
  });

  const existingInspection = useMemo(
    () => normalizeInspection(inspectionData),
    [inspectionData]
  );

  const { mutateAsync: createInspection, isPending: isCreating } =
    useCreateInspectionMutation();
  const { mutateAsync: updateInspection, isPending: isUpdating } =
    useUpdateInspectionMutation();

  const saving = isCreating || isUpdating;
  const inspectionId = getEntityId(existingInspection);

  useEffect(() => {
    if (!show) return;
    setFormError("");
    setForm(createEmptyInspectionForm(existingInspection));
  }, [show, existingInspection]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!registrationId || readOnly || saving) return;

    const payload = buildInspectionPayload(registrationId, form);
    if (!Object.keys(payload.data).length) {
      setFormError("Fill at least one inspection field before saving.");
      return;
    }

    try {
      setFormError("");
      if (inspectionId) {
        await updateInspection({
          inspectionId,
          payload: { data: payload.data, replace: true },
        });
      } else {
        await createInspection(payload);
      }
      await refetch();
      onSaved?.();
      onClose?.();
    } catch (err) {
      setFormError(
        getRegistrationApiErrorMessage(err, "Failed to save inspection.")
      );
    }
  };

  const extraKeys = Object.keys(form).filter(
    (key) => !INSPECTION_FIELD_DEFS.some((field) => field.key === key)
  );

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {inspectionId ? "Edit inspection" : "Inspection form"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {registration ? (
            <p className="text-muted mb-3">
              Driver: <strong>{getDriverName(registration)}</strong>
              {" · "}
              Team: <strong>{getTeamName(registration)}</strong>
            </p>
          ) : null}

          {isLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : isError && !existingInspection ? (
            <Alert variant="warning" className="mb-3">
              {getRegistrationApiErrorMessage(
                error,
                "No existing inspection found. You can create one below."
              )}
            </Alert>
          ) : null}

          {formError ? (
            <Alert variant="danger" className="mb-3">
              {formError}
            </Alert>
          ) : null}

          {!isLoading ? (
            <Row>
              {INSPECTION_FIELD_DEFS.map((field) => (
                <Col
                  md={field.type === "textarea" ? 12 : 6}
                  key={field.key}
                  className="mb-3"
                >
                  <Form.Group>
                    <Form.Label>{field.label}</Form.Label>
                    {field.type === "select" ? (
                      <Form.Select
                        value={form[field.key] || ""}
                        disabled={readOnly || saving}
                        onChange={(e) =>
                          handleChange(field.key, e.target.value)
                        }
                      >
                        {(field.options || []).map((option) => (
                          <option key={option.value || "empty"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : field.type === "textarea" ? (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={form[field.key] || ""}
                        disabled={readOnly || saving}
                        onChange={(e) =>
                          handleChange(field.key, e.target.value)
                        }
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        value={form[field.key] || ""}
                        disabled={readOnly || saving}
                        onChange={(e) =>
                          handleChange(field.key, e.target.value)
                        }
                      />
                    )}
                  </Form.Group>
                </Col>
              ))}

              {extraKeys.map((key) => (
                <Col md={6} key={key} className="mb-3">
                  <Form.Group>
                    <Form.Label>{key}</Form.Label>
                    <Form.Control
                      type="text"
                      value={form[key] || ""}
                      disabled={readOnly || saving}
                      onChange={(e) => handleChange(key, e.target.value)}
                    />
                  </Form.Group>
                </Col>
              ))}
            </Row>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          {!readOnly ? (
            <Button type="submit" disabled={saving || isLoading || !registrationId}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : inspectionId ? (
                "Update inspection"
              ) : (
                "Save inspection"
              )}
            </Button>
          ) : null}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default InspectionModal;
